// External libraries
import { isFunction } from '@checkout/react-components';

// Internal modules
import { appStore } from '@/store';
import { $logger, ACCOUNT_CREATION_PAYLOAD } from '@/lib/logger';
import { conditionallyDropLoginCookie, getLocale } from '@/lib';

/**
 * @function responseContingencyHandlerFactory
 * @returns {object} Factory function which returns object to handle 3ds
 */
const responseContingencyHandlerFactory = () => {
  return data => {
    const { flags, paymentContingencies } = data;

    if (
      flags?.is3DSecureRequired &&
      paymentContingencies?.threeDomainSecure?.status !== 'SUCCESS'
    ) {
      // If 3DS status isn't 'SUCCESS' then there was a failure
      // and we need to throw to trigger error handling
      $logger.error('3ds_status_not_success');
      throw new Error('WEASLEY_3DS_ERROR');
    }

    return {
      is3DSecureRequired: Boolean(flags?.is3DSecureRequired),
      threeDS1ContingencyData: paymentContingencies?.threeDomainSecure,
      threeDS2ContingencyData: paymentContingencies?.threeDSContingencyData,
    };
  };
};

/**
 * @function responseHandlerFactory
 * @param {object} state
 * @returns {object} factory function which
 * returns response data from the api downstreams.
 */
const responseHandlerFactory = namespace => {
  return response => {
    const { api: apiState, form: formState } = appStore.getState();
    const { value: onboardOption } = formState.onboardOption;
    const isGuest = onboardOption === 'guest';
    const responseData = response?.data?.[namespace];
    const { accessToken } = responseData?.buyer.auth || {};
    const { api: apiActions } = appStore.getActions();
    const fundingOptions = responseData.fundingOptions;
    if (fundingOptions) {
      const instrumentId = fundingOptions[0]?.fundingInstrument.id || '';
      apiActions.setInstrumentId(instrumentId);
    }

    // Save access token in store. Not dropping the login cookie until
    // after we check for errors because if we throw an error we'll drop
    // the cookie there
    if (accessToken) {
      const type = isGuest ? 'guest' : 'signup';
      const { cartAmount, merchantId } = apiState;
      if (isFunction(window.PAYPAL?.analytics?.instance?.recordClick)) {
        const data = ACCOUNT_CREATION_PAYLOAD({
          buyerId: responseData?.buyer?.userId,
          cartAmount,
          locale: getLocale(),
          merchantId,
        });

        $logger.info('account_creation_marketing_log', data);
        try {
          // Wrapping this in a try catch because it was blowing up the
          // when we first tried using it. It's used for marketing and it
          // shouldn't kill the flow if it fails.
          window.PAYPAL.analytics.instance.recordClick({ data });
        } catch (error) {
          const { message, stack: stackTrace } = error;
          $logger.warn('paypal_analytics_record_click_error', {
            message,
            stackTrace,
          });
        }
      }

      $logger.info('access_token_present', { accountType: type });
    }

    // In the incontext flow update the parent window's access token.
    if (isFunction(window.xprops?.onAuth)) {
      $logger.info('update_incontext_with_access_token');
      window.xprops.onAuth({ accessToken });
    }

    return conditionallyDropLoginCookie(responseData);
  };
};

const onAfterOnboardAccountCreationHandler = responseHandlerFactory(
  'onboardAccount',
);
const onAfterPaymentPlanCreationHandler = responseHandlerFactory(
  'createOnboardingPaymentPlan',
);
const onAfterOnboardAccountSuccessHandler = responseContingencyHandlerFactory();
const onAfterPaymentPlanSuccessHandler = responseContingencyHandlerFactory();

export {
  onAfterOnboardAccountCreationHandler,
  onAfterOnboardAccountSuccessHandler,
  onAfterPaymentPlanCreationHandler,
  onAfterPaymentPlanSuccessHandler,
};
